import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApprovalDecision } from '../../types/chat';
import { useBizyChatActions } from '../context/actions-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { RECOGNITION_DRAFT_FOCUS_TAB_PROPS } from '../lib/recognition-draft-focus-tab-props';
import { BizyRecognitionInline } from '../lib/recognition-inline';
import type { BizyNodeProps } from './node-props';

interface DraftVariant {
	tone: string;
	recipients?: string[];
	amount?: number;
	message?: string;
	hashtags?: string[];
}

type DraftCardPayload = {
	variants?: DraftVariant[];
	confirmLabel?: string;
	confirmMessage?: string;
};

// Backend-parsed protocol string — intentionally kept in English (not routed
// through i18n).
function buildConfirmMessage(variant: DraftVariant): string {
	const parts: string[] = [];
	if (variant.recipients?.length) parts.push(variant.recipients.map((r) => `@${r}`).join(' '));
	if (variant.amount) parts.push(`+${variant.amount}`);
	if (variant.message) parts.push(variant.message);
	if (variant.hashtags?.length) parts.push(variant.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' '));
	return `Yes, send it: ${parts.join(' ')}`;
}

function ToneTab({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) {
	return (
		<Box
			as="button"
			role="tab"
			aria-selected={isActive}
			onClick={onClick}
			borderRadius={8}
			cursor="pointer"
			transition="all 0.15s ease"
			bg={isActive ? 'bg.surface' : 'transparent'}
			color={isActive ? 'text.default' : 'text.softer'}
			boxShadow={isActive ? 'sm' : 'none'}
			_hover={{ color: 'text.default' }}
			{...RECOGNITION_DRAFT_FOCUS_TAB_PROPS}
		>
			{label}
		</Box>
	);
}

/**
 * Legacy `draft_card` node — Bizy offers a drafted recognition with one or
 * more tone variants. "Give now" sends the confirm message back to Bizy;
 * "Edit in give box" hands off to the host give-box via the injected
 * `onOpenGiveBox` (web opens the give-box; desktop opens a Bonusly link).
 */
export function DraftCardNode({ node, cardIndex }: BizyNodeProps) {
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const { messageKey, nodeChoices, onConfirm, onChoiceUpdate, onOpenGiveBox, isReadOnly, followUpsDisabled } = useBizyChatActions();
	const p = (node as { payload: DraftCardPayload }).payload;
	const variants = p.variants ?? [];
	const [activeIndex, setActiveIndex] = useState(0);
	const externalChoice = nodeChoices?.[cardIndex];
	const [localChoice, setLocalChoice] = useState<ApprovalDecision | undefined>();
	const choice = externalChoice ?? localChoice;
	const isLocked = Boolean(choice) || isReadOnly || followUpsDisabled;

	const activeVariant = variants[activeIndex] ?? variants[0];

	const recordChoice = useCallback(() => {
		setLocalChoice('approved');
		if (messageKey && onChoiceUpdate) onChoiceUpdate(messageKey, cardIndex, 'approved');
	}, [cardIndex, messageKey, onChoiceUpdate]);

	const handleEditInGiveBox = useCallback(() => {
		if (isLocked || !activeVariant) return;
		recordChoice();
		onOpenGiveBox?.({
			recipients: activeVariant.recipients,
			amount: activeVariant.amount,
			message: activeVariant.message,
			hashtags: activeVariant.hashtags?.map((h) => (h.startsWith('#') ? h.slice(1) : h)),
		});
	}, [activeVariant, isLocked, onOpenGiveBox, recordChoice]);

	const handleGiveNow = useCallback(() => {
		if (isLocked || !activeVariant) return;
		recordChoice();
		const confirmMessage = buildConfirmMessage(activeVariant);
		if (confirmMessage && onConfirm) onConfirm(cardIndex, confirmMessage);
	}, [activeVariant, cardIndex, isLocked, onConfirm, recordChoice]);

	if (!variants.length) return null;

	return (
		<Box my={2}>
			<motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
				<Box border="1px solid" borderColor="border.default" borderRadius={12} bg="bg.surface" overflow="hidden">
					{variants.length > 1 && (
						<Flex role="tablist" gap={1} px={2} pt={2} pb={1}>
							<Flex bg="bg.subtle" borderRadius={10} p={0.5} gap={0.5}>
								{variants.map((v, i) => (
									<ToneTab key={v.tone} label={v.tone} isActive={i === activeIndex} onClick={() => setActiveIndex(i)} />
								))}
							</Flex>
						</Flex>
					)}

					<Box px={3} py={3}>
						<Text fontSize="sm" color="text.default" lineHeight="tall">
							<BizyRecognitionInline
								recipients={activeVariant.recipients}
								amount={activeVariant.amount}
								message={activeVariant.message}
								hashtags={activeVariant.hashtags}
							/>
						</Text>
					</Box>

					<Flex align="center" justify="flex-end" gap={2} px={3} pb={3}>
						<Button size="sm" variant="solid" colorScheme="blue" onClick={handleGiveNow} isDisabled={isLocked} flexShrink={0}>
							{t('draft_card.give_now')}
						</Button>
						<Button size="sm" variant="solid" colorScheme="gray" onClick={handleEditInGiveBox} isDisabled={isLocked} flexShrink={0}>
							{p.confirmLabel ?? t('draft_card.edit_in_give_box')}
						</Button>
					</Flex>
				</Box>
			</motion.div>
		</Box>
	);
}
